import { AVPFeaturePullApartKey } from '@/store/modules/Constants'

export interface StandardObject {
  [key: string]: string | boolean | number
}

export interface ApiV2Jig {
  FormatVersion: number;
  JigMetaData: JigMetaData;
  JigModelData: JigModelData;
  JigStageData: JigStageData;
  JigSceneData: JigSceneData;
}

export interface TenantJigPermissions {
  TenantID: number;
  Permissions: string[];
  Type?: string;
}

export interface FeatureOption {
  enabled: boolean
  settingType: string
}

export interface JigFeatureOption {
  [AVPFeaturePullApartKey]: FeatureOption
}

export interface JigFeatures {
  options: JigFeatureOption
}

export interface JigMetaData {
  Author: string;
  Categories: string[];

  CopyableBy: string;

  DateCreated: string;
  DatePublished: string;
  DateUpdated: string;
  DeeplinkURL?: string;
  DeeplinkURLRequesting?: boolean;

  FormatVersion: number;
  FormatVersionAtCreation: number;
  GlobalScale?: number;
  Id: string;
  MovoEnabled?: boolean;
  ProjectDescription: string;
  ProjectName: string;
  ProjectVisibility: string;
  RequestUserPermission: Permission;
  OwnerTenantID?: number;
  Password?: string;
  PasswordDateCreated?: string;
  Tags: string[];
  Tenants?: TenantJigPermissions[];
  ThumbnailURL: string;
  Type: string;
  Uid: string;
  Version: number;
  Views: number;
  votes: number;
  Features: JigFeatures;
}

export interface CRUD {
  C?: boolean;
  R?: boolean;
  D?: boolean;
  U?: boolean;
}

export interface NullableTime {
  Time: Date;
  Valid: boolean;
}

export interface Permission {
  ID: number;
  CRUD: CRUD;
  ResID: number;
  ResType: PermissionsResourceType;
  AudID: number;
  AudName: string;
  AudType: PermissionsAudienceType;
  ExpiresAt: NullableTime;
}

export enum PermissionsResourceType {
  Jig = 0,
}

export enum PermissionsAudienceType {
  User = 0,
}

export interface PermissionView {
  Index: number;
  Perm: Permission;
}

export interface PermissionAddView {
  AudName: string;
  AudType: PermissionsAudienceType;
  R: boolean;
  U: boolean;
  Valid: boolean;
}

export interface JigPermissionCreateRequest {
  UrlParams: {
    email: string;
    type: string;
  };
  JigId: string;
}

export interface InviteCreateParams {
  email: string;
  resourceHashID: string; // Either this or resourceID needs to be populated
  resourceID: number;
  resourceType: PermissionsResourceType;
  permActions: PermAction[];
}

export interface InviteResource {
  resourceHashID: string; // Either this or resourceID needs to be populated
  resourceID: number;
  resourceType: PermissionsResourceType
  permActions: PermAction[];
}

export interface Invite {
  id: number;
  inviteeEmail: string;
  inviterUserID: string;
  tenantID: number;
  inviteeRole: string;
  resources: InviteResource[];
  outcomes: string[];
  status: string;
  expiresAt: any;
  createdAt: Date;
  updatedAt: Date;
  app: TargetApp;
}

export interface InviteCreateRequest {
  invitation: Invite;
}

export interface InviteCreateBulkRequest {
  requests: InviteCreateRequest[];
}

export interface InviteCreateResponse {
  invitation: Invite;
  user: any;
  tenantUser: TenantUser
}

export interface InviteCreateBulkItemResponse {
  response: InviteCreateResponse;
  errorMsg: string;
}

export interface InviteCreateBulkResponse {
  responses: InviteCreateBulkItemResponse[];
}

export interface JigPermissionDeleteRequest {
  ResID: number;
  PermID: number;
}

export interface JigPermissionUpdateRequest {
  JigId: string;
  Perm: Permission;
}

export interface JigModelData {
  ModelName: string;
  GeometryString: string;
  HasTexture: boolean;
  TextureString: string;
  Materials: Material[];
  Meshes: Mesh[];
  Models: Model[];
  Textures: any[];
}

export interface Material {
  UID: string;
  Id: number;
  Name: string;
  EmissiveColor: Color;
}

export interface Color {
  r: number;
  g: number;
  b: number;
  a: number;
}

export interface Mesh {
  UID: string;
  Name: string;
  MaterialId: number;
  MaterialUID: string;
  TextureUID: string;
}

export interface Model {
  UID: string;
  OriginalUID: string;
  Uri: string;
  FullFileName: string;
  GeometryString: string;
  HasTexture: boolean;
  IsFlipped: boolean;
  SubMeshes: Mesh[];
  Textures: any[];
  GeometryCanBeIgnored: boolean;
}

export interface JigSceneData {
  BackgroundImage: string;
  BackgroundColor: Color;
}

export interface JigStageData {
  Stages: Stage[];
}

export interface Stage {
  StageInstructions: string;
  StagePosition: StagePosition;
  StageSound: Sound;
  StageRotation: StagePosition;
  StageScale: StagePosition;
  Loop: boolean;
  IsViewpointActiveInStage: boolean;
  ViewpointDistance: number;
  ViewpointPosition: StagePosition;
  ViewpointRotation: StagePosition;
  ViewpointTargetPosition: StagePosition;
  SerializedElements: SerializedElement[];
}

export interface SerializedElement {
  ElementIndex: number;
  ElementUniqueName: string;
  ModelUID: string;
  ElementData: ElementData;
}

export interface ElementData {
  ObjectName: string;
  Label: string;
  LabelOnForThisStage: boolean;
  ShowcaseOnThisStage: boolean;
  MaterialUID: string;
  TextureUID: string;
  ParentName: string;
  ParentObject: ParentObject;
  ParentModelUID: string;
  Color: Color;
  EmissiveColor: Color;
  Metallic: number;
  Smoothness: number;
  Normal: number;
  Height: number;
  Occlusion: number;
  Sound: Sound;
  Position: StagePosition;
  Rotation: StagePosition;
  Scale: StagePosition;
  AutoSpinAxis: StagePosition;
  AutoSpinSpeed: number;
  AutoSpinOnThisStage: boolean;
  Actions: any[];
}

export interface StagePosition {
  x: number;
  y: number;
  z: number;
  w?: number;
}

export interface ParentObject {
  instanceID: number;
}

export interface Sound {
  Uri: string;
  Delay: number;
  Loop: boolean;
}

export interface CopyJigResult {
  id: string;
  name: string;
}

export interface LibraryEntry {
  Id: number;
  UID: number;
  Visibility: string;
  Version: string;
  Author: string;
  DisplayName: string;
  Description: string;
  ThumbnailUrl: string;
  Tags: string[];
  Categories: number[];
  DateCreated: string;
  DateUpdated: string;
  Item: LibraryItem;
  TenantId: number;
}

export interface LibraryItem {
  Id: number;
  FileName: string;
  ItemType: string;
  ItemUrl: string;
  ParentingMap: ParentingMap[];
  LabelMap: LabelMap[];
  MaterialMap: MaterialMap[];
  Textures: any[];
}

export interface LabelMap {
  SubMeshName: string;
  Label: string;
}

export interface MaterialMap {
  MaterialId: number;
  SubMeshName: string;
  Color: Color;
  Emissive: Color;
  Roughness: number;
  Metallic: number;
  Occlusion: number;
}

export interface ParentingMap {
  ChildName: string;
  ParentName: string;
}

export interface SaveJigResult {
  message: string;
  status: string;
  version: string;
}

export interface SaveJigVisibilityResult {
  status: string;
  visibility: string;
  Password: string;
  PasswordDateCreated: string;
}

export enum HTTPAction {
  GET,
  POST,
  PUT,
  DELETE,
}

export interface HTTPRequest {
  action: HTTPAction;
  url: string;
  body?: any;
  params?: any;
  responseType?: any;
  cancelToken?: any;
}

export interface JigCategoryOption {
  text: string;
  value: string;
}

export interface LibraryCategories {
  Categories: LibraryCategory[];
  CategoryOrder: number[];
}

export interface LibraryCategory {
  CategoryId: number;
  DisplayName: string;
  CategoryOrder: number;
  StateId: number;
  StateName: string;
}

export interface LibraryCategoryStates {
  States: CategoryState[];
}

export interface CategoryState {
  StateId: number;
  StateName: string;
}

export interface ChangeJigOwnerRequest {
  JigId: string;
  DesiredOwnerEmail: string;
  DesiredOwnerTenantID?: number;
  action?: string;
}

export interface ChangeOwnerTenantRequest {
  JigId: string;
  DesiredOwnerTenantID: number;
}

export interface ChangeJigOwnerResult {
  message: string;
  status: string;
}

// MyJig is the data returned from action 'loadMyJigs' and it has different naming to JigMetaData
// even though it is essentially almost the same data.
export interface MyJig {
  author: string;
  categories: string[];
  data: any;
  dateCreated: string;
  datePublished: string;
  dateUpdated: string;
  deeplinkURL?: string;
  deeplinkURLRequesting: boolean;
  description: string;
  formatVersion: number;
  globalScale?: number;
  id: string;
  metrics: JigMetrics;
  movoEnabled?: boolean;
  name: string;
  ownerTenantID?: number;
  password?: string;
  passwordDateCreated?: string;
  requestUserPermission: Permission;
  tags: string[];
  thumbnailURL: string;
  type: string;
  uid: string;
  version: number;
  versionParts: string;
  views: number;
  visibility: string;
  voted: boolean;
  votes: number;
}

// TeamJig follows the camel case format of MyJig, it contains the combination of MyJig types and JigMetaData types.
// The reason for this type is that JigsListing.vue is shared between myJigs and TeamJigs functionalities. One returning V1 jig data and one returning V2 jig data.
// It's easier to merge to 1 data structure so component is easier to handle both data types.
export interface TeamJig {
  author: string;
  categories: string[];
  data?: any,
  dateCreated: string;
  datePublished: string;
  dateUpdated: string;
  deeplinkURL?: string;
  deeplinkURLRequesting?: boolean;
  description: string;
  formatVersion: number;
  globalScale?: number;
  id: string;
  metrics?: JigMetrics;
  movoEnabled?: boolean;
  name: string;
  ownerTenantID?: number;
  password?: string;
  passwordDateCreated?: string;
  requestUserPermission: Permission;
  tags: string[];
  Tenants?: TenantJigPermissions[];
  thumbnailURL: string;
  type: string;
  uid: string;
  version: number;
  versionParts?: string;
  views: number;
  visibility?: string;
  votes: number;
}

export interface JigVisibilities {
  jigId: string;
  visibility: string;
  regenPassword: boolean;
  version: number;
}

export interface JigMetrics {
  views: number;
  avgViewingTimeSeconds: number;
  avgViewingTime: string;
  totalViewingTimeSeconds: number;
  totalViewingTime: string;
  uniqueUsersPresentingJig: number;
}

export type TargetApp = 'viewer' | 'workshop' | 'jigspace';

export interface JigShareRequest {
  jig: MyJig|JigMetaData;
  branchIOCustomData: any;
  targetApp: TargetApp;
  route: string;
}

export interface JigShareURLData {
  jigId: string;
  requestInFlight: boolean;
  shareURL: string;
}

export interface UploadThumbnailRequest {
  Id: string;
  TenantId: number;
  Thumbnail: File;
  IsFirstThumbnail: boolean;
  version?: number;
  DateUpdated?: string;
}

export interface UploadThumbnailResult {
  status: string;
  thumbnailURL: string;
}

export interface QueueIdResult {
  queueId: string; // UUID
  originalFileName: string;
  status: string;
  message: string;
}

export interface QueuedStatusResult {
  queueId: string; // UUID
  processingType: string; // 'undefined', 'astc_library_entry_thumbnail', 'obj_assimp' etc
  status: string; // 'queued', 'in-progress', 'done', 'failed'
  originalFile: string;
  files: FileGenerated[];
  params: any; // map of string:string
  errorMessage: string;
  wasRead: boolean;
  dateCreated: string; // golang time.Time
}

export interface FileGenerated {
  filename: string;
  fileType: string;
  fileExtension: string;
  fileUrl: string;
}

export interface ImageValidationResult {
  valid: boolean;
  x: number;
  y: number;
  xMin: number;
  xMax: number;
  yMin: number;
  yMax: number;
}

export interface GatedFeature {
  name: string
  value: string
}

export interface Featuregate {
  enabled: boolean
  GatedFeatures?: GatedFeature[]
}

export interface SubscriptionFeatures {
  avp: Featuregate
  movo: Featuregate
  stats: JigStats
}

export interface JigStats {
  jigStats: Featuregate
}

export interface TenantSubscription {
  AdditionalSeats?: number;
  AllowCustomBranding: boolean;
  AllowMovoJigs: boolean;
  AllowJigStats: boolean;
  AllowPasswordJigs: boolean;
  AllowPrivateJigs: boolean;
  AllowWorkshopPropertyPanel: boolean;
  ManagedByType: string;
  MaxJigs: number;
  MaxJigsCountPer: string;
  ModelUploadMaxSizeMB: number;
  OverLimitWarningDismissableUntil: string;
  OverLimitWarningShowContactUs: boolean;
  PricingSet: string;
  SeatsIncludedInTier?: number | string;
  StripeCustomerId: string;
  StripeSubscriptionId: string;
  SubscriptionFeatures: SubscriptionFeatures
  TeamStorageLimitMB: number;
  TierType: string;
  TierTypeDisplayName: string;
  ViewJigStepMax: number;
  RestrictedJigCopyableByTypes: string[];
}

export interface Tenant {
  ID: number;
  Active: boolean;
  Name: string;
  CreatedAt: Date;
  UpdatedAt?: any;
  UserCount: number | undefined;
  ColorHex1: string;
  ColorHex2: string;
  // Deprecated. Even if the Logourl is set, it is always retrieved from one endpoint for all tenants, using the right x-tenant-id header. So this is deprecated.
  Logourl: string;
  // Deduced client side: this can be set as the src of an img. We format it like "data:image/png;base64,BASE_64_IMG_DATA_HERE"
  // Or if its default it can be set as "./img/JWPro_Logo_512.png"
  LogoImage: string;
  Subscription: TenantSubscription;
  JigFinishedCustomLink: string
  // TODO: This is a temporary solution. Idealy we could simply retrieve fresh logo via `GET https://api.jig.space/tenants/v1/images/v1`
  // Based on response code 200 or 304 to identify if tenant logo has been updated or it's a cloudfront cached file.
  // Or can check logo status by checking if Logourl contains a refresh index.
  // When either of above is available, remove this tag and re-implement.
  LogoUpdated?: boolean
}

export interface TenantSearchResponse {
  limit: number
  page: number
  total_rows: number
  total_pages: number
  rows: Tenant[]
}

export interface TenantLogoImageResponse {
  ID: number;
  ImageData: string;
}

export interface TenantCreateRequest {
  Name: string;
  ColorHex1: string;
  ColorHex2: string;
}

export interface PermissionsMap {
  [name: string]: { [action: string]: any };
}

export interface TenantUser {
  ID: number;
  Uid: number;
  dateCreated: Date;
  dateUpdated?: Date;
  email: string;
  name: string;
  roles: string[];
  status: string;
  tenantId: number;
  username: string;
  permissions: PermissionsMap;
  // Deduced client side
  extraPermissionNames: string[];
}

export interface TenantUserCountData {
  count: number;
}

export interface TenantUserCountResponse {
  Data: TenantUserCountData;
  DataOp?: string;
  Fileds?: string;
  Filter?: string;
  PageNo: number;
  PageSize: number;
  SortOrder?: string;
}

export interface TenantToken {
  id: number;
  rol: string[];
  permissions: PermissionsMap;
  billing_source: string;
  billing_status: string;
  // Deduced client side
  name: string;
  extraPermissionNames: string[];
  // deprecated - use billing_source and billing_status
  paid_up: boolean;
  tenant_type: string;
}

export interface GetTenantsByUserRequest {
  UrlParams: {
    all: string;
  };
  ModalLoad: boolean;
}

export interface TenantUserAddRequest {
  TenantId: number;
  UrlParams: {
    email: string;
    extra_perms: string;
    role: string;
  };
}

export interface TenantUserUpdateRequest {
  TenantId: number;
  UrlParams: {
    Uid: number;
    extra_perms: string;
    role: string;
  };
}

export interface TenantUserRemoveRequest {
  TenantId: number;
  Uid: number;
}

export interface TenantsPaginationPayload {
  page: number;
  itemsPerPage: number;
  pageStart?: number;
  pageStop?: number;
  pageCount?: number;
  itemsLength?: number;
}

export interface UserRoleDefinition {
  Name: string;
  DisplayName: string;
  RestrictToTenantIds: number[];
}

export type PermName = string;
export type PermAction = 'view' | 'manage' | 'billing_contact' | 'login' | 'edit'
  | 'create' | 'delete' | 'copy' | 'small' | 'medium' | 'large' | 'offline';

export class PermissionActions {
  public static readonly View: PermAction = 'view';
  public static readonly Manage: PermAction = 'manage';
  public static readonly BillingContact: PermAction = 'billing_contact';
  public static readonly Login: PermAction = 'login';
  public static readonly Edit: PermAction = 'edit';
  public static readonly Create: PermAction = 'create';
  public static readonly Delete: PermAction = 'delete';
  public static readonly Copy: PermAction = 'copy';
  public static readonly Offline: PermAction = 'offline';
  public static readonly Small: PermAction = 'small';
  public static readonly Medium: PermAction = 'medium';
  public static readonly Large: PermAction = 'large';
}

export interface SecurityPermission {
  Name: PermName;
  Actions: PermAction[];
}

export interface JigSummaryStats {
  id: number;
  statisticDate: string;
  application: string;
  jigId: number;
  jigName: string;
  activity: string;
  total: number;
  durationSeconds: number;
  duration: string;
}

export interface ReportJigSummaryStats {
  activity: string
  author: string
  dataAvailable: boolean
  jigId: number
  jigName: string
  statisticDate: string
  total: number
  duration?: string
  durationSeconds?: number
}

export interface ReportJigHighlightStats {
  [key: string]: number
}

export interface JigActivityChartData {
  data: {
    datasets: Array<{
      backgroundColor?: string;
      data?: any[];
      label?: string,
      borderColor?: string,
      pointBackgroundColor?: string,
      borderWidth?: number,
      pointBorderColor?: string,
    }>;
    labels: any[]
  };
  options: {
    responsive: boolean;
    maintainAspectRatio: boolean;
    elements?: {
      point?: {
        radius?: number,
      },
    },
    title: {
      display: boolean
      text: string
    };
    legend: {
      display?: boolean
    },
    tooltips: {
      enabled: boolean
    },
    scales?: {
      xAxes?: [{
        type?: string,
        ticks?: {
          autoSkip?: boolean,
          autoSkipPadding?: number,
          maxRotation?: number,
          min?: number,
        },
      }],
      yAxes?: [{
        type?: string,
        ticks?: {
          autoSkip?: boolean,
          autoSkipPadding?: number,
          maxRotation?: number,
          min?: number,
        },
      }],
    },
  };
}

export interface TenantDashboardData {
  HighlightStats: ReportJigHighlightStats | null
  ComparisonHighlightStats?: ReportJigHighlightStats | null
  JigSummaryStats: ReportJigSummaryStats[] | null
  ComparisonJigSummaryStats?: ReportJigSummaryStats[] | null
}

export interface TenantDashboardReport {
  RequestByTenant: number
  RequestByUser: number
  ReportName: string
  ReportStartDate: string
  ReportEndDate: string
  ReportDays: number
  ProvideHighlightStats: boolean
  ProvideComparisons: boolean
  GenerateNullMissingValues: boolean
  Activities: string[]
  JigIds: number[]
  UsersIds: string[]
  Data: TenantDashboardData
}

export interface JigSummaryComparisonStats {
  JigSummaryStats: JigSummaryStats[];
  ComparisonProvided: boolean;
  ComparisonTrend: number;
}

export interface JigReportData {
  ReportName: string;
  Data: any[];
}

export interface ReportRequest {
  Name: string;
  Limit: number;
  LookBackDays: number;
  StartDate: string; // API wants a Date string in RFC3339 nano format
  EndDate: string; // API wants a Date string in RFC3339 nano format
  ComparePreviousPeriod: boolean;
  JigHashId: string;
}

export interface UploadTenantLogoRequest {
  TenantId: number;
  Image: File;
}

export interface SetupIntentResponse {
  stripeCustomerId: string;
  stripeSetupIntentId: string;
  stripeSetupIntentClientSecret: string;
}

export interface CreateCheckoutResponse {
  CheckoutURL: string
}

export interface CreateCustomerPortalResponse {
  PortalURL: string
}

export interface TenantSetupJob {
  idToken: string;
  tenantToCreate: TenantCreateRequest;
  customerEmail: string;
  customerPassword: string;
  customerUid: number;
  status: string;
  errorMsg: string;
  latestHostedInvoiceUrl: string;
}

export interface DesktopVersion {
  ID: number;
  Application: string;
  Platform: string;
  Name: string;
  Archived: boolean;
  CreatedAt: string;
  UpdatedAt: string;
  SupportStart: string;
  SupportEnd: string;
  ExpiryReminderStartOffsetHRS: number;
  UpgradeCheckFrequencyHRS: number;
  UpgradeReminderMessage: string;
  Version: string;
  Build: number;
  downloadFileUri: string;
}

export interface EmbedQueryDependsOn {
  name: string;
  value: number;
}

export interface EmbedQueryParams {
  name: string;
  displayName: string;
  defaultValue: string|number;
  valueType: string;
  minValue?: number;
  maxValue?: number;
  descriptionShort: string;
  descriptionLong: string;
  listOrder: number;
  prefillName?: string;
  dependsOnParam?: EmbedQueryDependsOn;
}

export interface EmbedQueryParamObject {
  'en-US': Array<EmbedQueryParams>
}

export interface EmbedQueryParamConfig {
  defaultUrl: string;
  defaultIframeEmbedHtml: string;
  backlinkHtml: string;
  queryParamDocs: EmbedQueryParamObject;
}

export interface PricingTier {
  additionalSeatPrices?: any
  allowCustomBranding?: boolean
  allowJigActions: any
  allowJigStats?: boolean
  allowPasswordJigs?: boolean
  allowPrivateJigs?: boolean
  allowWorkshopPropertyPanel?: boolean
  displayName: string
  maxJigs ?: number
  maxJigsCountPer?: string
  initialCheckoutPrices?: any
  modelUploadMaxSizeMB: number
  name: string
  overLimitWarningDismissableUntil: string
  overLimitWarningShowContactUs: boolean
  restrictedJigCopyableByTypes: string[]
  seatsIncluded: number
  teamStorageLimitMB: number
  viewJigStepMax: number
}

export interface PricignListItem {
  name: string
  tiers: {
    [key:string]: PricingTier
  }
}

export interface DynaconfConfig {
  pricing: {
    sets: {
      [key: string]: PricignListItem
    }
  }
}
